import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
 Card,
 CardContent,
 CardHeader,
 CardTitle,
 CardDescription,
} from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { 
 Clock, 
 CheckCircle, 
 Search, 
 Plus, 
 Briefcase, 
 Users, 
 MapPin, 
 Calendar,
 Filter,
 BarChart3,
 ChevronRight
} from "lucide-react";
import api from "../../api/client";
import { isJobExpired, isPublishedJob } from "../../utils/jobSlug";
import { glassCard, glassCardHover } from "../../utils/glassStyles";
import RecruiterPageShell from "../../components/recruiter/RecruiterPageShell";
import RecruiterStatCard from "../../components/recruiter/RecruiterStatCard";
import JobStatusBadge from "../../components/recruiter/JobStatusBadge";
import Loader from "../../components/ui/Loader";
import { Bar } from "react-chartjs-2";
import {
 Chart as ChartJS,
 CategoryScale,
 LinearScale,
 BarElement,
 Title,
 Tooltip,
 Legend,
} from "chart.js";

ChartJS.register(
 CategoryScale,
 LinearScale,
 BarElement,
 Title,
 Tooltip,
 Legend
);

export default function RecruiterJobsOverview() {
 const navigate = useNavigate();
 const [jobs, setJobs] = useState([]);
 const [filteredJobs, setFilteredJobs] = useState([]);
 const [loading, setLoading] = useState(true);
 const [search, setSearch] = useState("");
 const [statusFilter, setStatusFilter] = useState("ALL");
 const [currentPage, setCurrentPage] = useState(1);
 const itemsPerPage = 6;

 useEffect(() => {
 const fetchJobs = async () => {
 try {
 const res = await api.get("/recruiter/jobs");
 setJobs(res.data);
 } catch (err) {
 console.error(err);
 } finally {
 setLoading(false);
 }
 };
 fetchJobs();
 }, []);

 const handleFilterAndSearch = useCallback(() => {
 let data = [...jobs];

 if (statusFilter !== "ALL") {
 data = data.filter((j) => j.status === statusFilter);
 }

 if (search.trim()) {
 data = data.filter(
 (j) =>
 j.title.toLowerCase().includes(search.toLowerCase()) ||
 j.location.toLowerCase().includes(search.toLowerCase())
 );
 }

 setFilteredJobs(data);
 setCurrentPage(1);
 }, [jobs, statusFilter, search]);

 useEffect(() => {
 handleFilterAndSearch();
 }, [handleFilterAndSearch]);

 const stats = useMemo(() => {
 const total = jobs.length;
 const approved = jobs.filter(j => isPublishedJob(j)).length;
 const expired = jobs.filter(j => isJobExpired(j)).length;
 const pending = jobs.filter(j => j.status === "PENDING").length;
 const rejected = jobs.filter(j => j.status === "REJECTED").length;
 const totalApplicants = jobs.reduce((acc, job) => acc + (job.applications?.length || 0), 0);
 
 return { total, approved, expired, pending, rejected, totalApplicants };
 }, [jobs]);

 const chartData = useMemo(() => {
 return {
 labels: ["Pending", "Approved", "Rejected"],
 datasets: [
 {
 label: "Jobs by Status",
 data: [stats.pending, stats.approved, stats.rejected],
 backgroundColor: [
 "rgba(250, 204, 21, 0.6)", // yellow
 "rgba(34, 197, 94, 0.6)", // green
 "rgba(239, 68, 68, 0.6)", // red
 ],
 borderColor: [
 "rgb(250, 204, 21)",
 "rgb(34, 197, 94)",
 "rgb(239, 68, 68)",
 ],
 borderWidth: 2,
 borderRadius: 8,
 hoverBackgroundColor: [
 "rgba(250, 204, 21, 0.8)",
 "rgba(34, 197, 94, 0.8)",
 "rgba(239, 68, 68, 0.8)",
 ],
 },
 ],
 };
 }, [stats]);

 const totalPages = Math.ceil(filteredJobs.length / itemsPerPage);
 const paginatedJobs = filteredJobs.slice(
 (currentPage - 1) * itemsPerPage,
 currentPage * itemsPerPage
 );

 const getStatusBadge = (status, job) => (
   <JobStatusBadge status={status} job={job} />
 );

 if (loading) {
   return <Loader label="Loading your jobs..." />;
 }

 return (
   <RecruiterPageShell
     title="Manage Your Jobs"
     subtitle="Monitor, track, and manage all your job postings"
     onBack={() => navigate("/recruiter/dashboard")}
     actions={
       <Button onClick={() => navigate("/recruiter/post-job")} size="lg" className="shadow-lg">
         <Plus className="w-5 h-5 mr-2" />
         Post New Job
       </Button>
     }
   >
     <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
       <RecruiterStatCard title="Total Jobs" value={stats.total} icon={Briefcase} accent="blue" />
       <RecruiterStatCard title="Published" value={stats.approved} icon={CheckCircle} accent="green" />
       <RecruiterStatCard title="Pending" value={stats.pending} icon={Clock} accent="yellow" />
     </div>

     <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
 {/* Analytics Chart */}
 <motion.div 
 initial={{ opacity: 0, x: -20 }}
 animate={{ opacity: 1, x: 0 }}
 className="lg:col-span-1"
 >
 <Card className={`h-full rounded-2xl overflow-hidden ${glassCard}`}>
 <CardHeader className="pb-2">
 <div className="flex items-center gap-2">
 <BarChart3 className="w-5 h-5 text-primary" />
 <CardTitle>Job Statistics</CardTitle>
 </div>
 <CardDescription>Visual breakdown by status</CardDescription>
 </CardHeader>
 <CardContent className="h-[300px] flex items-center justify-center p-6">
 <Bar
 data={chartData}
 options={{
 responsive: true,
 maintainAspectRatio: false,
 plugins: {
 legend: { display: false },
 tooltip: {
 backgroundColor: "rgba(0, 0, 0, 0.8)",
 padding: 12,
 cornerRadius: 8,
 titleFont: { size: 14, weight: 'bold' },
 bodyFont: { size: 13 }
 }
 },
 scales: {
 x: { grid: { display: false }, ticks: { color: "hsl(var(--muted-foreground))" } },
 y: { 
 beginAtZero: true, 
 grid: { color: "rgba(255, 255, 255, 0.05)" },
 ticks: { color: "hsl(var(--muted-foreground))", precision: 0 } 
 },
 },
 }}
 />
 </CardContent>
 </Card>
 </motion.div>

 {/* Jobs List with Search & Filter */}
 <motion.div 
 initial={{ opacity: 0, y: 20 }}
 animate={{ opacity: 1, y: 0 }}
 transition={{ delay: 0.2 }}
 className="lg:col-span-2 space-y-6"
 >
 <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
 <div className="relative w-full sm:max-w-md group">
 <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground group-focus-within:text-primary transition-colors" />
 <Input
 placeholder="Search jobs by title or location..."
 value={search}
 onChange={(e) => setSearch(e.target.value)}
 className="pl-10 bg-card/30 backdrop-blur-sm border-border/50 focus:ring-primary h-11 transition-all duration-300"
 />
 </div>
 <div className="flex items-center gap-2 w-full sm:w-auto">
 <div className="p-2 rounded-lg bg-card/50 border border-border/50">
 <Filter className="w-4 h-4 text-muted-foreground" />
 </div>
 <select
 value={statusFilter}
 onChange={(e) => setStatusFilter(e.target.value)}
 className="bg-card/30 backdrop-blur-sm text-foreground border border-border/50 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 flex-1 sm:w-40 transition-all duration-300"
 >
 <option value="ALL">All Statuses</option>
 <option value="PENDING">Pending</option>
 <option value="APPROVED">Approved</option>
 <option value="EXPIRED">Expired</option>
 <option value="CLOSED">Closed</option>
 <option value="REJECTED">Rejected</option>
 </select>
 </div>
 </div>

 <div className="space-y-4">
 <AnimatePresence mode="popLayout">
 {paginatedJobs.length > 0 ? (
 paginatedJobs.map((job, idx) => (
 <JobRow 
 key={job.id} 
 job={job} 
 navigate={navigate} 
 getStatusBadge={getStatusBadge}
 index={idx}
 />
 ))
 ) : (
 <motion.div
 initial={{ opacity: 0 }}
 animate={{ opacity: 1 }}
 className="text-center py-20 bg-card/20 rounded-2xl border-2 border-dashed border-border/50"
 >
 <Briefcase className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
 <h3 className="text-xl font-semibold">No jobs found</h3>
 <p className="text-muted-foreground">Try adjusting your search or filters.</p>
 </motion.div>
 )}
 </AnimatePresence>
 </div>

 {/* Pagination */}
 {totalPages > 1 && (
 <div className="flex justify-center items-center gap-2 pt-4">
 <Button
 variant="outline"
 size="sm"
 disabled={currentPage === 1}
 onClick={() => setCurrentPage((prev) => prev - 1)}
 className="bg-card/50 backdrop-blur-sm border-border/50"
 >
 Previous
 </Button>
 <div className="flex items-center gap-1">
 {Array.from({ length: totalPages }, (_, i) => (
 <Button
 key={i}
 variant={currentPage === i + 1 ? "premium" : "ghost"}
 size="sm"
 onClick={() => setCurrentPage(i + 1)}
 className="w-10 h-10"
 >
 {i + 1}
 </Button>
 ))}
 </div>
 <Button
 variant="outline"
 size="sm"
 disabled={currentPage === totalPages}
 onClick={() => setCurrentPage((prev) => prev + 1)}
 className="bg-card/50 backdrop-blur-sm border-border/50"
 >
 Next
 </Button>
 </div>
 )}
 </motion.div>
 </div>
   </RecruiterPageShell>
 );
}

function JobRow({ job, navigate, getStatusBadge, index }) {
 return (
 <motion.div
 layout
 initial={{ opacity: 0, y: 10 }}
 animate={{ opacity: 1, y: 0 }}
 exit={{ opacity: 0, scale: 0.95 }}
 transition={{ delay: index * 0.05 }}
 whileHover={{ scale: 1.01 }}
 className="group"
 >
 <Card 
 className={`rounded-2xl cursor-pointer overflow-hidden group/card ${glassCardHover}`}
 onClick={() => navigate(`/recruiter/jobs/${job.id}`)}
 >
 <CardContent className="p-0">
 <div className="flex flex-col sm:flex-row sm:items-center justify-between p-5 gap-4">
 <div className="flex items-start gap-4 flex-1">
 <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center border border-primary/20 transition-all duration-300 group-hover/card:bg-primary/20 group-hover/card:scale-110">
 <Briefcase className="w-6 h-6 text-primary" />
 </div>
 <div className="space-y-1">
 <h3 className="font-bold text-lg text-foreground group-hover/card:text-primary transition-colors">{job.title}</h3>
 <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground font-medium">
 <div className="flex items-center gap-1.5">
 <MapPin className="w-3.5 h-3.5" />
 {job.location}
 </div>
 <div className="flex items-center gap-1.5">
 <Calendar className="w-3.5 h-3.5" />
 {new Date(job.createdAt).toLocaleDateString()}
 </div>
 <div className="flex items-center gap-1.5">
 <Users className="w-3.5 h-3.5" />
 {job.applications?.length || 0} applicants
 </div>
 </div>
 </div>
 </div>
 
 <div className="flex items-center justify-between sm:justify-end gap-4 border-t sm:border-t-0 pt-4 sm:pt-0 border-border/50">
 <div className="flex flex-col items-start sm:items-end gap-1">
 {getStatusBadge(job.status, job)}
 <span className="text-[10px] uppercase tracking-widest text-muted-foreground/60 font-bold px-1">
 Posted Status
 </span>
 </div>
 
 <div 
 onClick={(e) => {
 e.stopPropagation();
 navigate(`/recruiter/jobs/${job.id}/applicants`);
 }}
 className="w-10 h-10 rounded-xl bg-card/80 border border-border/50 flex items-center justify-center hover:bg-primary hover:text-white transition-all duration-300 group/btn shadow-sm"
 title="View Applicants"
 >
 <ChevronRight className="w-5 h-5 group-hover/btn:translate-x-0.5 transition-transform" />
 </div>
 </div>
 </div>
 </CardContent>
 </Card>
 </motion.div>
 );
}
